import React, { useState } from 'react';
import { 
  X, Package, Edit2, Trash2, TrendingUp, TrendingDown,
  Hash, Ruler, DollarSign, BarChart3, AlertTriangle, 
  CheckCircle2, XCircle, FileText, Calendar, ZoomIn
} from 'lucide-react';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF', SEK: 'kr', NOK: 'kr', DKK: 'kr',
  MXN: '$', BRL: 'R$', NZD: 'NZ$', PLN: 'zł', CZK: 'Kč'
};

const InventoryDetailModal = ({ 
  isOpen, onClose, item, onEdit, onDelete, currency = 'USD'
}) => {
  const [showFullImage, setShowFullImage] = useState(false);
  
  if (!isOpen || !item) return null;

  const currencySymbol = CURRENCY_SYMBOLS[currency] || '$';
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    if (currency === 'JPY') return `${currencySymbol}${Math.round(num)}`;
    return `${currencySymbol}${num.toFixed(2)}`;
  };

  const getCost = () => parseFloat(item.cost || item.msrp || 0);
  const getDisplayName = () => (item.name && item.name.trim()) ? item.name : item.item;

  const parseSizes = () => {
    if (Array.isArray(item.sizes)) return item.sizes;
    if (item.size && typeof item.size === 'string' && item.size.startsWith('[')) {
      try { return JSON.parse(item.size); } catch { return null; }
    }
    return null;
  };

  const sizes = parseSizes();
  const hasSizes = sizes && sizes.length > 0;

  const getTotalQuantity = () => {
    if (hasSizes) return sizes.reduce((sum, s) => sum + (parseInt(s.quantity) || 0), 0);
    return parseInt(item.quantity) || 0;
  };

  const totalQuantity = getTotalQuantity();
  const reorderPoint = parseInt(item.reorderPoint || 5);

  const getStockStatus = () => {
    if (totalQuantity === 0) return { label: 'Out of Stock', bgClass: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400', barClass: 'bg-red-500' };
    if (hasSizes) {
      const outCount = sizes.filter(s => parseInt(s.quantity) === 0).length;
      const lowCount = sizes.filter(s => { const q = parseInt(s.quantity); return q > 0 && q <= reorderPoint; }).length;
      if (outCount > 0) return { label: `${outCount} Size(s) Out`, bgClass: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400', barClass: 'bg-red-500' };
      if (lowCount > 0) return { label: `${lowCount} Size(s) Low`, bgClass: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400', barClass: 'bg-yellow-500' };
    } else if (totalQuantity <= reorderPoint) {
      return { label: 'Low Stock', bgClass: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400', barClass: 'bg-yellow-500' };
    }
    return { label: 'In Stock', bgClass: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400', barClass: 'bg-green-500' };
  };

  const getSizeStatus = (qty) => {
    if (qty === 0) return 'out';
    if (qty <= reorderPoint) return 'low';
    return 'ok';
  };

  const getMargin = () => {
    const cost = getCost();
    const sell = parseFloat(item.sellPrice || 0);
    if (sell <= 0) return { value: 0, isPositive: false, profit: 0 };
    const margin = ((sell - cost) / sell) * 100;
    return { value: margin.toFixed(1), isPositive: margin >= 0, profit: sell - cost };
  };

  const status = getStockStatus();
  const margin = getMargin();
  const cost = getCost();
  const totalValue = totalQuantity * parseFloat(item.sellPrice || 0);
  const totalProfit = totalValue - (totalQuantity * cost);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header with close button */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{getDisplayName()}</h2>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${status.bgClass}`}>
                {status.label}
              </span>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Product Image - Contained properly */}
            {item.imageUrl ? (
              <div 
                className="relative bg-gray-100 dark:bg-gray-900 cursor-pointer group"
                onClick={() => setShowFullImage(true)}
              >
                <div className="w-full flex items-center justify-center p-4">
                  <img 
                    src={item.imageUrl} 
                    alt={getDisplayName()} 
                    className="max-w-full max-h-64 object-contain rounded-lg"
                  />
                </div>
                {/* Zoom hint overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-white/90 dark:bg-gray-800/90 px-3 py-2 rounded-lg flex items-center gap-2">
                    <ZoomIn size={16} />
                    <span className="text-sm font-medium">Tap to zoom</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
                <Package size={48} className="text-gray-400" />
              </div>
            )}

            <div className="p-5">
              {/* Item type subtitle */}
              {item.name && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{item.item}</p>
              )}
              
              {/* Size/SKU info */}
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400 text-sm mb-4">
                {hasSizes ? (
                  <span className="flex items-center gap-1">
                    <Ruler size={14} />
                    {sizes.length} sizes
                  </span>
                ) : (
                  <>
                    <span className="flex items-center gap-1">
                      <Ruler size={14} />
                      {item.size || 'N/A'}
                    </span>
                    {item.sku && (
                      <span className="flex items-center gap-1 font-mono text-xs">
                        <Hash size={14} />
                        {item.sku}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Size Breakdown */}
              {hasSizes && (
                <div className="mb-5 bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <Ruler size={16} />
                    Size Breakdown
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {sizes.map((sizeData) => {
                      const qty = parseInt(sizeData.quantity) || 0;
                      const sizeStatus = getSizeStatus(qty);
                      return (
                        <div 
                          key={sizeData.size} 
                          className={`p-3 rounded-lg border text-center ${
                            sizeStatus === 'out' 
                              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                              : sizeStatus === 'low' 
                                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' 
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold text-gray-800 dark:text-gray-100">{sizeData.size}</span>
                            {sizeStatus === 'out' && <XCircle size={14} className="text-red-500" />}
                            {sizeStatus === 'low' && <AlertTriangle size={14} className="text-yellow-500" />}
                            {sizeStatus === 'ok' && <CheckCircle2 size={14} className="text-green-500" />}
                          </div>
                          <p className={`text-lg font-bold ${
                            sizeStatus === 'out' 
                              ? 'text-red-600 dark:text-red-400' 
                              : sizeStatus === 'low' 
                                ? 'text-yellow-600 dark:text-yellow-400' 
                                : 'text-gray-800 dark:text-gray-100'
                          }`}>
                            {qty}
                          </p>
                          {sizeData.sku && (
                            <p className="text-xs text-gray-400 font-mono mt-1">#{sizeData.sku}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
                    <Package size={14} />
                    Quantity
                  </div>
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{totalQuantity}</p>
                  <p className="text-xs text-gray-400">Reorder at ≤{reorderPoint}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
                    <DollarSign size={14} />
                    Price
                  </div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(item.sellPrice)}
                  </p>
                  <p className="text-xs text-gray-400">Cost: {formatCurrency(cost)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
                    {margin.isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    Margin
                  </div>
                  <p className={`text-2xl font-bold ${
                    margin.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {margin.value}%
                  </p>
                  <p className="text-xs text-gray-400">{formatCurrency(margin.profit)}/unit</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
                    <BarChart3 size={14} />
                    Total Value
                  </div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {formatCurrency(totalValue)}
                  </p>
                  <p className="text-xs text-gray-400">Profit: {formatCurrency(totalProfit)}</p>
                </div>
              </div>

              {/* Notes */}
              {item.notes && (
                <div className="mb-5">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-2">
                    <FileText size={14} />
                    Notes
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm border border-gray-200 dark:border-gray-700">
                    {item.notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions - Fixed at bottom */}
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-4 flex gap-3 bg-white dark:bg-gray-800">
            <button 
              onClick={() => { onEdit(item); onClose(); }} 
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
            >
              <Edit2 size={18} />
              Edit Item
            </button>
            <button 
              onClick={() => { onDelete(item); onClose(); }} 
              className="px-5 py-3 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-xl font-medium transition-colors"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Full Screen Image Viewer */}
      {showFullImage && item.imageUrl && (
        <div 
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <button 
            onClick={() => setShowFullImage(false)}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          <img 
            src={item.imageUrl} 
            alt={getDisplayName()} 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default InventoryDetailModal;